import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import Ornament from "@/components/Ornament";
import MilleniumBadge from "@/components/MilleniumBadge";
import api from "@/lib/api";
import SeoHead from "@/components/SeoHead";

const HOME_SEO_TITLES = {
  fr: "Prieuré saint Léonard — Vains, Baie du Mont-Saint-Michel",
  en: "Priory of Saint Léonard — Vains, Mont-Saint-Michel Bay",
  it: "Priorato di San Leonardo — Vains, Baia di Mont-Saint-Michel",
  es: "Priorato de San Leonardo — Vains, Bahía del Mont-Saint-Michel",
  de: "Priorat Saint Léonard — Vains, Bucht des Mont-Saint-Michel",
  pt: "Priorado de São Leonardo — Vains, Baía do Monte Saint-Michel",
  zh: "圣莱奥纳尔修道院 — 凡,圣米歇尔山湾",
  pl: "Klasztor św. Leonarda — Vains, Zatoka Mont-Saint-Michel",
};
const HOME_SEO_DESC = {
  fr: "Au croisement de la route des Ducs et de la Via Angelica, le Prieuré saint Léonard rouvre ses portes. Festes Millenium 2027, hospitalité, mécénat.",
  en: "At the crossroads of the Dukes' way and the Via Angelica, the Priory of Saint Léonard reopens its doors. Millenium 2027 Feasts, hospitality, patronage.",
  it: "All'incrocio della via dei Duchi e della Via Angelica, il Priorato di San Leonardo riapre le porte. Feste Millenium 2027, ospitalità, mecenatismo.",
  es: "En el cruce del camino de los Duques y la Via Angelica, el Priorato de San Leonardo reabre sus puertas. Fiestas Millenium 2027, hospitalidad, mecenazgo.",
  de: "An der Kreuzung des Herzogsweges und der Via Angelica öffnet das Priorat Saint Léonard wieder seine Türen. Feste Millenium 2027, Gastfreundschaft, Mäzenatentum.",
  pt: "No cruzamento da rota dos Duques e da Via Angelica, o Priorado de São Leonardo reabre as suas portas. Festas Millenium 2027, hospitalidade, mecenato.",
  zh: "公爵之路与天使之路的交汇处,圣莱奥纳尔修道院重新开放。千禧 2027 节日、款待、赞助。",
  pl: "Na skrzyżowaniu Drogi Książąt i Via Angelica, Klasztor św. Leonarda otwiera ponownie swoje drzwi. Święto Millenium 2027, gościnność, mecenat.",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const FALLBACK_HERO_SLIDES = [
  { src: "/photos/prieure-accueil.jpg", alt: "Prieuré saint Léonard" },
  { src: "/photos/vue-du-mont.jpg", alt: "Vue du Mont-Saint-Michel depuis le prieuré" },
  { src: "/photos/mont-bay-aerial.jpg", alt: "Baie du Mont-Saint-Michel" },
];
const HISTORY_IMG = "/photos/prieure-cross.jpg";
const BELL_IMG = "/photos/cloche.jpg";
const BAY_IMG = "/photos/mont-bay-aerial.jpg";

export default function Home() {
  const { t, locale } = useI18n();
  const [events, setEvents] = useState([]);
  const [homePhotos, setHomePhotos] = useState([]);
  const [slide, setSlide] = useState(0);
  const [brokenSlides, setBrokenSlides] = useState(() => new Set());

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/photos", { params: { on_home: true } })
      .then((r) => setHomePhotos(r.data.items || [])).catch(() => {});
  }, []);

  // Use admin-managed photos if available, else fall back to the bundled set.
  // Filter out images that previously failed to load.
  const ALL_HERO_SLIDES = homePhotos.length > 0
    ? homePhotos.map((p) => ({
        src: resolveAsset(p.image_url),
        alt: pickLocalized(p.caption || {}, locale) || "Prieuré saint Léonard",
      }))
    : FALLBACK_HERO_SLIDES;
  const HERO_SLIDES = ALL_HERO_SLIDES.filter((s) => !brokenSlides.has(s.src));
  // Safety fallback: if every admin photo broke, use bundled set
  const SLIDES = HERO_SLIDES.length > 0 ? HERO_SLIDES : FALLBACK_HERO_SLIDES;

  useEffect(() => {
    if (SLIDES.length <= 1) return undefined;
    setSlide((s) => Math.min(s, SLIDES.length - 1));
    const id = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
    return () => clearInterval(id);
  }, [SLIDES.length]);

  return (
    <div data-testid="home-page">
      <SeoHead titles={HOME_SEO_TITLES} descriptions={HOME_SEO_DESC} image="/photos/prieure-meadow.jpg"
        keywords="Prieuré saint Léonard, Vains, Mont-Saint-Michel, baie du Mont-Saint-Michel, monument historique, Normandie, millenium 2027, hébergement, hôtel, traversée de la baie, abbayes de Normandie route historique, patrimoine normand, pèlerinage, concerts, artistes" />
      {/* HERO with crossfade slideshow */}
      <section className="relative" style={{ height: "100vh", minHeight: 600 }}>
        {SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms]"
            style={{ opacity: slide === i ? 1 : 0 }}
            onError={() => {
              setBrokenSlides((prev) => {
                if (prev.has(s.src)) return prev;
                const next = new Set(prev);
                next.add(s.src);
                return next;
              });
            }}
          />
        ))}
        <div className="absolute inset-0 hero-vignette" />

        {/* PENDANT du badge Millenium — accès direct aux Fêtes internationales
            de saint Léonard, positionné en bas à gauche du hero pour équilibrer
            la composition visuelle avec le badge Millenium (bas-droite). */}
        <Link
          to="/presence"
          data-testid="hero-fetes-badge"
          aria-label={locale === "fr" ? "Fêtes populaires de saint Léonard — millénaire 2027" : "Popular Feasts of Saint Léonard — millennium 2027"}
          className="absolute z-10 group"
          style={{
            left: "clamp(0.75rem, 3vw, 2rem)",
            bottom: "clamp(1rem, 4vw, 2.5rem)",
            background: "rgba(255,255,255,0.95)",
            padding: "10px 14px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.7)",
            transition: "transform 220ms ease, box-shadow 220ms ease",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            textDecoration: "none",
            height: "clamp(3rem, 7vw, 5rem)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 22px rgba(0,0,0,0.42)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.35)";
          }}
        >
          <img
            src="/logo-prieure.png"
            alt=""
            style={{ height: "100%", width: "auto", objectFit: "contain", flexShrink: 0 }}
          />
          <div className="flex flex-col leading-none">
            <span
              className="font-caps text-[0.55rem] sm:text-[0.62rem]"
              style={{ color: "#c1272d", letterSpacing: "0.16em", fontWeight: 600 }}
            >
              MILLENIUM · 2027
            </span>
            <span
              className="font-display text-sm sm:text-base mt-0.5"
              style={{ color: "var(--ink)", lineHeight: 1.1, whiteSpace: "nowrap" }}
            >
              {locale === "fr" ? "Fêtes populaires de saint Léonard" : t.nav.presence}
            </span>
          </div>
        </Link>

        <div className="container-prieure relative h-full flex flex-col justify-end pb-6 sm:pb-10">
          <div className="scroll-fade flex justify-end max-w-full">
            <div className="flex items-center gap-3 sm:gap-7 max-w-full" data-testid="hero-title-row">
              <div className="text-right min-w-0 flex-1 sm:flex-initial">
                <p className="font-display italic text-base sm:text-2xl lg:text-3xl text-white leading-tight"
                   style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
                   data-testid="hero-subtitle">
                  {t.home.heroSubtitle}
                </p>
                <p className="font-display italic text-base sm:text-2xl lg:text-3xl text-white/90 leading-tight mt-1"
                   style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
                   data-testid="hero-tagline">
                  {t.home.heroTagline}
                </p>
              </div>
              <img
                src="/photos/millenium-vertical.png"
                alt="Millenium 2027 — Année Européenne des Normands"
                data-testid="home-millenium-badge"
                style={{
                  background: "rgba(255,255,255,0.95)",
                  padding: "6px",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                  border: "1px solid rgba(255,255,255,0.7)",
                  aspectRatio: "1 / 1",
                  height: "clamp(3rem, 7vw, 5rem)",
                  width: "auto",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* NEXT EVENT — discreet countdown band */}
      <NextEventBanner events={events} locale={locale} />

      {/* FÊTES 2027 — live countdown band */}
      <FetesCountdownBand locale={locale} />

      {/* INTRO */}
      <section className="py-24 sm:py-32 section-edge-top">
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Ornament className="justify-start mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl leading-tight" style={{ color: "var(--ink)" }}>
              {t.home.introTitle}
            </h2>
            {Array.isArray(t.home.introBody) ? (
              <div className="mt-6 space-y-4">
                {t.home.introBody.map((para, i) => (
                  <p key={i} className="font-body text-lg" style={{ color: "var(--ink-2)" }}>
                    {para}
                  </p>
                ))}
              </div>
            ) : (
              <p className="font-body text-lg mt-6" style={{ color: "var(--ink-2)" }}>
                {t.home.introBody}
              </p>
            )}
          </div>
          <div className="md:col-span-5">
            <div className="illuminated-border" style={{ background: "var(--parchment-2)" }}>
              <div className="label-caps mb-4" style={{ color: "var(--cinnabar)" }}>{t.home.prayerTitle}</div>
              {t.home.prayerLines.map((line, i) => (
                <p key={i} className="font-display italic text-2xl leading-snug mb-3" style={{ color: "var(--ink)" }}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section id="histoire" className="py-24 sm:py-32" style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure">
          <div className="max-w-3xl mb-16">
            <div className="font-caps text-[0.72rem] mb-4" style={{ color: "var(--cinnabar)" }}>
              {t.home.historyKicker}
            </div>
            <h2 className="font-display text-4xl sm:text-6xl leading-tight">{t.home.historyTitle}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 md:sticky md:top-24">
              <img src={HISTORY_IMG} alt="" className="w-full object-cover" style={{ aspectRatio: "4/5" }} />
              <p className="font-body italic text-sm mt-3" style={{ color: "var(--muted)" }}>
                {locale === "fr"
                  ? "Arcades de pierre — architecture monastique normande"
                  : "Stone arcades — Norman monastic architecture"}
              </p>
            </div>
            <div className="md:col-span-7 space-y-12">
              {t.home.historyChapters.map((c, i) => (
                <article key={i} data-testid={`history-chapter-${i}`} className="border-l-2 pl-6"
                         style={{ borderColor: i === 0 ? "var(--cinnabar)" : "var(--gold)" }}>
                  <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--ink-2)" }}>
                    {`I${"I".repeat(i)}`.slice(0, 5) /* simple roman feel */}
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl mb-3">{c.title}</h3>
                  <p className="font-body text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
                    {c.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED ARTICLE — BAPTÊME */}
      <section className="py-24 sm:py-32">
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="font-caps text-[0.72rem] mb-4" style={{ color: "var(--gold)" }}>
              {t.home.featuredArticleKicker}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">
              {t.home.featuredArticleTitle}
            </h2>
            <p className="font-body text-lg mt-6" style={{ color: "var(--ink-2)" }}>
              {t.home.featuredArticleBody}
            </p>
            <Link to="/bapteme-cloche" data-testid="featured-article-link" className="btn btn-gold mt-8">
              {t.common.readMore}
            </Link>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <img src={BELL_IMG} alt="Cloche sainte Anne" className="w-full object-cover" style={{ aspectRatio: "1/1" }} />
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="py-24 sm:py-32" style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="font-caps text-[0.72rem] mb-4" style={{ color: "var(--cinnabar)" }}>
                {t.home.eventsKicker}
              </div>
              <h2 className="font-display text-4xl sm:text-5xl">{t.home.eventsTitle}</h2>
            </div>
            <Link to="/evenements" data-testid="home-events-cta" className="btn btn-outline">
              {t.home.eventsCta}
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {events.map((e) => (
              <Link
                key={e.id}
                to={`/evenements/${e.id}`}
                data-testid={`home-event-${e.id}`}
                className="group bg-white border block transition"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="overflow-hidden" style={{ aspectRatio: "4/3" }}>
                  <img src={e.image_url} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="font-caps text-[0.65rem] mb-2" style={{ color: "var(--gold)" }}>
                    {new Date(e.date).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", {
                      day: "numeric", month: "long", year: "numeric"
                    })}
                  </div>
                  <h3 className="font-display text-2xl leading-tight mb-2">{pickLocalized(e.title, locale)}</h3>
                  <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>
                    {pickLocalized(e.location, locale)}
                  </p>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    {e.ticketed && (
                      <span className="tag tag-cinnabar">
                        {t.events.ticketedTag}
                      </span>
                    )}
                    {(pickLocalized(e.title, locale) || "").toLowerCase().includes("ostension") && (
                      <span className="tag tag-gold">UNESCO</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRESENCE TEASER */}
      <section className="relative py-32" style={{ background: "var(--ink)" }}>
        <img src={HISTORY_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="container-prieure relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="font-caps text-[0.72rem] mb-4" style={{ color: "#D4AF37" }}>
              {t.home.presenceKicker}
            </div>
            <h2 className="font-display text-5xl sm:text-7xl text-white leading-none mb-6"
                style={{ letterSpacing: "0.02em" }}>
              {t.home.presenceTitle}
            </h2>
            <p className="font-body text-lg text-white/80 max-w-2xl">
              {t.home.presenceBody}
            </p>
          </div>
          <div className="md:col-span-5 md:text-right flex md:justify-end items-center gap-6 flex-wrap">
            <div className="bg-white/95 px-4 py-3" style={{ borderRadius: 4 }}>
              <MilleniumBadge size={56} variant="horizontal" />
            </div>
            <Link to="/presence" data-testid="home-presence-cta" className="btn btn-primary">
              {t.home.presenceCta}
            </Link>
          </div>
        </div>
      </section>

      {/* VIVA ARTIST teaser — pont vers le label depuis le trafic principal */}
      <section
        className="relative py-24 sm:py-28"
        style={{ background: "var(--parchment-2, #f4ecd8)" }}
        data-testid="home-viva-teaser"
      >
        <div className="container-prieure relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          {/* Image — rai de lumière sculptant l'ombre */}
          <div className="md:col-span-5 order-1 md:order-1">
            <div
              className="relative w-full overflow-hidden"
              style={{ aspectRatio: "4 / 3", border: "1px solid var(--gold)" }}
            >
              <img
                src="/photos/viva-artist-hero-1600.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              {/* subtle warm veil so the image reads calm rather than dramatic here */}
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(180deg, rgba(20,16,12,0.10) 0%, rgba(20,16,12,0.25) 100%)",
                }}
                aria-hidden="true"
              />
            </div>
          </div>
          {/* Text + CTA */}
          <div className="md:col-span-7 order-2 md:order-2">
            <div
              className="font-caps text-[0.72rem] mb-4"
              style={{ color: "var(--gold-dark, #a68b3d)", letterSpacing: "0.18em" }}
              data-testid="home-viva-kicker"
            >
              {t.home.vivaKicker}
            </div>
            <h2
              className="font-display text-4xl sm:text-6xl leading-none mb-6"
              style={{ color: "var(--ink)", letterSpacing: "0.02em" }}
              data-testid="home-viva-title"
            >
              {t.home.vivaTitle}
            </h2>
            <p
              className="font-body text-lg max-w-2xl"
              style={{ color: "var(--muted)" }}
            >
              {t.home.vivaBody}
            </p>
            <div className="mt-8">
              <Link
                to="/viva-artist"
                data-testid="home-viva-cta"
                className="btn btn-primary"
              >
                {t.home.vivaCta} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SUPPORT CTA */}
      <section className="relative py-32" style={{ background: "var(--ink)" }}>
        <img src={BAY_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="container-prieure relative text-center">
          <div className="font-caps text-[0.72rem] mb-4" style={{ color: "var(--gold)" }}>
            {t.home.supportKicker}
          </div>
          <h2 className="font-display text-4xl sm:text-6xl text-white max-w-3xl mx-auto">
            {t.home.supportTitle}
          </h2>
          <p className="font-body text-lg text-white/80 max-w-2xl mx-auto mt-6">
            {t.home.supportBody}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/association" data-testid="home-cta-join" className="btn btn-primary">
              {t.common.join}
            </Link>
            <Link to="/mecenat" data-testid="home-cta-donate"
                  className="btn btn-outline" style={{ color: "white", borderColor: "white" }}>
              {t.common.donate}
            </Link>
          </div>
        </div>
      </section>

      {/* Journal du projet — section retirée */}
    </div>
  );
}

// ============================== Fêtes 2027 countdown band ==============================
const FETES_DATE = new Date("2027-07-01T17:00:00+02:00");
const FETES_LABELS = {
  fr: { kicker: "Fêtes populaires de saint Léonard · Millénaire 2027", days: "Jours", hours: "Heures", minutes: "Min", seconds: "Sec", opened: "Les Fêtes ont commencé", cta: "Découvrir les Fêtes" },
  en: { kicker: "Popular Feasts of Saint Léonard · Millennium 2027", days: "Days", hours: "Hours", minutes: "Min", seconds: "Sec", opened: "The Feasts have begun", cta: "Discover the Feasts" },
  it: { kicker: "Feste popolari di San Leonardo · Millennio 2027", days: "Giorni", hours: "Ore", minutes: "Min", seconds: "Sec", opened: "Le Feste sono iniziate", cta: "Scoprire le Feste" },
  es: { kicker: "Fiestas populares de San Leonardo · Milenio 2027", days: "Días", hours: "Horas", minutes: "Min", seconds: "Seg", opened: "Las Fiestas han comenzado", cta: "Descubrir las Fiestas" },
  de: { kicker: "Volksfeste des heiligen Leonhard · Millennium 2027", days: "Tage", hours: "Std", minutes: "Min", seconds: "Sek", opened: "Die Feste haben begonnen", cta: "Die Feste entdecken" },
  pt: { kicker: "Festas populares de São Leonardo · Milénio 2027", days: "Dias", hours: "Horas", minutes: "Min", seconds: "Seg", opened: "As Festas começaram", cta: "Descobrir as Festas" },
  zh: { kicker: "圣莱奥纳尔民间节庆 · 千禧 2027", days: "天", hours: "时", minutes: "分", seconds: "秒", opened: "节庆已开始", cta: "探索节庆" },
  pl: { kicker: "Święta ludowe św. Leonarda · Millenium 2027", days: "Dni", hours: "Godz", minutes: "Min", seconds: "Sek", opened: "Święta się rozpoczęły", cta: "Odkryj Święta" },
  ru: { kicker: "Народные праздники святого Леонарда · Миллениум 2027", days: "Дней", hours: "Час", minutes: "Мин", seconds: "Сек", opened: "Праздники начались", cta: "Открыть праздники" },
  uk: { kicker: "Народні свята святого Леонарда · Міленіум 2027", days: "Днів", hours: "Год", minutes: "Хв", seconds: "Сек", opened: "Свята розпочалися", cta: "Відкрити свята" },
  ro: { kicker: "Sărbătorile populare ale Sf. Leonard · Mileniul 2027", days: "Zile", hours: "Ore", minutes: "Min", seconds: "Sec", opened: "Sărbătorile au început", cta: "Descoperă Sărbătorile" },
};

function FetesCountdownBand({ locale }) {
  const labels = FETES_LABELS[locale] || FETES_LABELS.fr;
  const compute = () => {
    const diff = FETES_DATE.getTime() - Date.now();
    if (diff <= 0) return { over: true };
    return {
      over: false,
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [c, setC] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setC(compute()), 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const cells = c.over ? [] : [
    { v: c.days, l: labels.days, k: "days" },
    { v: pad(c.hours), l: labels.hours, k: "hours" },
    { v: pad(c.minutes), l: labels.minutes, k: "minutes" },
    { v: pad(c.seconds), l: labels.seconds, k: "seconds" },
  ];
  return (
    <section
      data-testid="home-fetes-countdown"
      className="border-b"
      style={{ background: "var(--parchment-2)", borderColor: "var(--border)" }}
    >
      <div className="container-prieure py-6 sm:py-7">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-8">
          <div className="min-w-0">
            <div className="font-caps text-[0.62rem] mb-3" style={{ color: "var(--cinnabar)", letterSpacing: "0.24em" }}>
              ◇ {labels.kicker.toUpperCase()} ◇
            </div>
            {c.over ? (
              <div className="font-display italic text-xl" style={{ color: "var(--ink)" }} data-testid="home-fetes-countdown-over">
                · {labels.opened} ·
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 sm:gap-3" data-testid="home-fetes-countdown-cells">
                {cells.map((cell) => (
                  <div
                    key={cell.k}
                    data-testid={`home-fetes-countdown-${cell.k}`}
                    className="flex flex-col items-center min-w-[58px] sm:min-w-[70px] px-3 py-2"
                    style={{ background: "var(--ink)", border: "1px solid var(--gold)" }}
                  >
                    <div className="font-display text-2xl sm:text-3xl tabular-nums leading-none" style={{ color: "var(--gold)" }}>
                      {cell.v}
                    </div>
                    <div className="font-caps text-[0.52rem] sm:text-[0.58rem] mt-1" style={{ color: "rgba(245,240,228,0.7)", letterSpacing: "0.2em" }}>
                      {cell.l.toUpperCase()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/fetes-populaires-2027"
            data-testid="home-fetes-countdown-cta"
            className="font-caps text-[0.65rem] whitespace-nowrap self-start sm:self-auto px-5 py-3 border transition-colors"
            style={{ color: "var(--cinnabar)", borderColor: "var(--cinnabar)", letterSpacing: "0.2em" }}
          >
            {labels.cta.toUpperCase()} →
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================== Next-event banner ==============================
const NEXT_EVENT_LABELS = {
  fr: { kicker: "Prochain événement", days: "j", today: "Aujourd'hui", cta: "Voir le programme" },
  en: { kicker: "Next event",         days: "d", today: "Today",         cta: "See programme" },
  it: { kicker: "Prossimo evento",    days: "g", today: "Oggi",          cta: "Vedi il programma" },
  es: { kicker: "Próximo evento",     days: "d", today: "Hoy",           cta: "Ver el programa" },
  de: { kicker: "Nächste Veranstaltung", days: "T", today: "Heute",      cta: "Programm ansehen" },
  pt: { kicker: "Próximo evento",     days: "d", today: "Hoje",          cta: "Ver o programa" },
  zh: { kicker: "下一场活动",          days: "天", today: "今天",          cta: "查看节目" },
  pl: { kicker: "Następne wydarzenie", days: "d", today: "Dzisiaj",      cta: "Zobacz program" },
  ru: { kicker: "Ближайшее событие",  days: "д", today: "Сегодня",       cta: "Программа" },
  uk: { kicker: "Найближча подія",    days: "д", today: "Сьогодні",       cta: "Програма" },
  ro: { kicker: "Următorul eveniment", days: "z", today: "Astăzi",        cta: "Vezi programul" },
};

function NextEventBanner({ events, locale }) {
  if (!events || events.length === 0) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  // Pick the soonest published event whose date is today or in the future.
  const upcoming = events
    .filter((e) => e.date && (e.published ?? true))
    .map((e) => ({ ...e, _d: new Date(`${e.date}T00:00:00`) }))
    .filter((e) => !Number.isNaN(e._d.getTime()) && e._d >= today)
    .sort((a, b) => a._d - b._d);
  if (upcoming.length === 0) return null;
  const next = upcoming[0];
  const labels = NEXT_EVENT_LABELS[locale] || NEXT_EVENT_LABELS.fr;
  const title = pickLocalized(next.title || {}, locale) || "";
  const location = pickLocalized(next.location || {}, locale) || "";
  const diffDays = Math.round((next._d - today) / 86400000);
  const dateStr = next._d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  return (
    <section
      data-testid="home-next-event"
      className="border-t border-b"
      style={{ background: "var(--ink)", color: "var(--parchment)", borderColor: "rgba(245,240,228,0.08)" }}
    >
      <div className="container-prieure py-6 sm:py-7">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-8">
          <div className="flex items-baseline gap-5 sm:gap-7 min-w-0">
            <div className="text-center shrink-0" data-testid="home-next-event-countdown">
              <div className="font-display text-3xl sm:text-4xl leading-none" style={{ color: "var(--gold)" }}>
                {diffDays === 0 ? "·" : `J−${diffDays}`}
              </div>
              <div className="font-caps text-[0.55rem] mt-1" style={{ color: "rgba(245,240,228,0.55)", letterSpacing: "0.2em" }}>
                {diffDays === 0 ? labels.today.toUpperCase() : labels.days}
              </div>
            </div>
            <div className="min-w-0">
              <div className="font-caps text-[0.6rem] mb-1" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
                ◇ {labels.kicker.toUpperCase()} ◇
              </div>
              <div className="font-display text-lg sm:text-xl leading-tight truncate" data-testid="home-next-event-title">
                {title}
              </div>
              <div className="font-body text-sm mt-1" style={{ color: "rgba(245,240,228,0.7)" }}>
                <span data-testid="home-next-event-date">{dateStr}</span>
                {next.time ? <span> · {next.time}</span> : null}
                {location ? <span> · {location}</span> : null}
              </div>
            </div>
          </div>
          <Link
            to="/presence/programme"
            data-testid="home-next-event-cta"
            className="font-caps text-[0.65rem] whitespace-nowrap self-start sm:self-auto px-5 py-3 border transition-colors"
            style={{ color: "var(--gold)", borderColor: "var(--gold)", letterSpacing: "0.2em" }}
          >
            {labels.cta.toUpperCase()} →
          </Link>
        </div>
      </div>
    </section>
  );
}
